import { useEffect, useState } from "react";
import { useSeo } from "@/hooks/useSeo";
import FaqSection from "@/components/faq-section";
import { PAGE_FAQS } from "@/data/faqs";
import { useBreadcrumbs } from "@/hooks/useBreadcrumbs";
import { openBookingModal } from "@/components/booking-modal";

export default function RomanticheskiyUzhin() {
  const [loaded, setLoaded] = useState(false);

  useSeo({
    title: "Романтический ужин на крыше в Петербурге — Паруса на крыше",
    description: "Романтический ужин на крыше Петербурга с панорамным видом 300° на город. Закаты над Невой, живая музыка, авторская кухня. Забронировать столик онлайн.",
    keywords: "романтический ужин Петербург, ужин на крыше СПб, свидание ресторан Петербург, романтическое свидание ресторан СПб, ужин с видом на город Петербург, закат ресторан Петербург",
    image: "/photos/view_sunset.jpg",
    canonical: "/romanticheskiy-uzhin",
  });
  useBreadcrumbs([{ name: "Романтический ужин", path: "/romanticheskiy-uzhin" }]);

  useEffect(() => { setTimeout(() => setLoaded(true), 100); }, []);

  return (
    <div className="w-full bg-background">
      {/* Hero */}
      <section className="relative h-[85vh] w-full flex items-center justify-center overflow-hidden border-b border-primary/20">
        <div className="absolute inset-0 z-0">
          <img src="/photos/view_sunset.webp" alt="Романтический ужин на крыше Петербурга" className="w-full h-full object-cover" style={{ filter: "brightness(0.3) saturate(0.7)", transform: "scale(1.05)" }} loading="eager" fetchPriority="high" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(3,13,9,0.98) 0%, rgba(5,20,10,0.7) 50%, rgba(5,20,10,0.4) 100%)" }} />
        </div>
        <div className="relative z-10 text-center px-6 max-w-3xl mx-auto" style={{ opacity: loaded ? 1 : 0, transform: loaded ? "translateY(0)" : "translateY(30px)", transition: "all 1.2s ease 0.3s" }}>
          <div className="inline-block px-6 py-2 border border-primary/30 mb-8">
            <span className="text-[9px] tracking-[0.3em] uppercase text-accent">Для двоих</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-serif font-light text-foreground mb-6 leading-[1.1]">
            Романтический ужин<br /><span className="text-accent italic">на крыше</span>
          </h1>
          <p className="text-white/60 text-lg font-light mb-12 max-w-lg mx-auto leading-relaxed">
            Романтический ужин на высоте — с видом на крыши города, который невозможно забыть
          </p>
          <button onClick={openBookingModal} className="bg-primary/20 border border-primary text-accent px-14 py-5 text-[10px] tracking-[0.35em] uppercase font-medium hover:bg-primary hover:text-background transition-colors">
            Забронировать столик
          </button>
        </div>
      </section>

      {/* Atmosphere */}
      <section className="py-32 bg-card border-b border-primary/10">
        <div className="container mx-auto px-6 md:px-14 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div>
              <div className="text-[9px] tracking-[0.4em] text-primary/50 uppercase mb-5">Атмосфера</div>
              <h2 className="text-4xl md:text-5xl font-serif font-light text-foreground mb-8 leading-tight">
                Вечер, который<br /><span className="text-accent italic">хочется повторить</span>
              </h2>
              <p className="text-muted-foreground font-light leading-relaxed mb-6 text-base">
                Живая музыка, мерцающие огни города за панорамными окнами, авторская кухня и особый коктейль — всё, чтобы вечер стал именно таким, каким должен быть.
              </p>
              <p className="text-muted-foreground font-light leading-relaxed mb-10">
                Наш персонал позаботится о каждой детали: лучший столик с видом, украшение стола, персональное меню — просто скажите нам, что вы хотите.
              </p>
              <button onClick={openBookingModal} className="border border-primary/40 text-accent px-10 py-4 text-[10px] tracking-[0.3em] uppercase hover:border-primary transition-colors">
                Выбрать дату →
              </button>
            </div>
            <div className="space-y-4">
              <img src="/photos/view_sunset.webp" alt="Закат над Петербургом" className="w-full aspect-video object-cover border border-primary/10" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* Details */}
      <section className="py-32 bg-background border-b border-primary/10">
        <div className="container mx-auto px-6 md:px-14 max-w-6xl">
          <div className="mb-16">
            <div className="text-[9px] tracking-[0.4em] text-primary/50 uppercase mb-4">Детали вечера</div>
            <h2 className="text-4xl md:text-5xl font-serif font-light text-foreground">Что делает ужин особенным</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: "✦", title: "Столик с видом", desc: "Лучшие места у панорамных окон — с закатом или ночными огнями города в зависимости от времени" },
              { icon: "✦", title: "Живая музыка", desc: "Джаз, лаунж, романтическая классика — живые выступления создают ту самую атмосферу" },
              { icon: "✦", title: "Авторская кухня", desc: "Меню из сезонных продуктов от шеф-повара — от лёгких закусок до изысканных горячих блюд" },
              { icon: "✦", title: "Подача шампанского", desc: "Встреча за бокалом игристого с видом на панораму — или выберите что-то из нашей винной карты" },
              { icon: "✦", title: "Декор на заказ", desc: "Лепестки роз, свечи, именная карточка — скажите нам заранее, и мы украсим стол" },
              { icon: "✦", title: "Особый повод", desc: "Годовщина, предложение руки и сердца, первое свидание — мы поможем сделать его незабываемым" },
            ].map((item) => (
              <div key={item.title} className="group p-6 border border-primary/15 hover:border-primary/40 transition-colors">
                <div className="text-primary/50 mb-4 text-sm">{item.icon}</div>
                <h3 className="font-['Cormorant_SC'] text-sm tracking-[0.15em] text-accent mb-3">{item.title}</h3>
                <p className="text-xs text-muted-foreground font-light leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Time */}
      <section className="py-24 bg-card border-b border-primary/10">
        <div className="container mx-auto px-6 md:px-14 max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-serif font-light text-foreground mb-6">Лучшее время для романтики</h2>
          <p className="text-muted-foreground font-light leading-relaxed mb-8 max-w-2xl mx-auto">
            Закат над крышами Петербурга начинается около 21:00 летом и 17:00 осенью. Забронируйте столик на это время — вид будет именно таким, как вы представляли.
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-[10px] uppercase tracking-widest text-muted-foreground/60">
            <span className="border border-primary/20 px-5 py-3">Пн–Чт · 12:00–23:00</span>
            <span className="border border-primary/20 px-5 py-3">Пт–Сб · 12:00–01:00</span>
            <span className="border border-primary/20 px-5 py-3">Вс · 12:00–23:00</span>
          </div>
        </div>
      </section>

      <FaqSection items={PAGE_FAQS["/romanticheskiy-uzhin"]} title="Вопросы о романтическом ужине" />

      {/* CTA */}
      <section className="py-28 bg-background">
        <div className="container mx-auto px-6 text-center max-w-2xl">
          <h2 className="text-4xl font-serif font-light text-foreground mb-6">Запланируйте вечер</h2>
          <p className="text-muted-foreground font-light mb-10 leading-relaxed">
            Забронируйте столик онлайн или позвоните нам, чтобы мы подготовили всё заранее.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={openBookingModal} className="bg-primary border border-primary text-background px-12 py-4 text-[10px] tracking-[0.3em] uppercase font-medium hover:bg-primary/80 transition-colors">
              Забронировать столик
            </button>
            <a href="tel:+78129911090" className="border border-primary/40 text-accent px-12 py-4 text-[10px] tracking-[0.3em] uppercase font-medium hover:border-primary transition-colors">
              +7 (812) 991-10-90
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
